import { z } from 'zod';

export const GRAPHICS_STORAGE_KEY = 'offworld.graphics.v1';
const scale = z.number().min(.5).max(1).refine(value => Math.abs(value * 20 - Math.round(value * 20)) < 1e-6);
const frameRate = z.union([z.literal(0),z.literal(30),z.literal(60),z.literal(120)]);
export const graphicsSchema = z.strictObject({
  renderScale: scale,
  shadows: z.enum(['off', 'low', 'medium', 'high']),
  localShadows: z.enum(['off','low','high']).default('low'),
  ambientOcclusion: z.enum(['off', 'low', 'high']),
  aoStrength: z.number().min(0).max(1).default(.65),
  reflections: z.enum(['low','medium','high']).default('medium'),
  indirectLighting: z.boolean().default(true),
  frameLimit: frameRate.default(0),
  targetFPS: frameRate.default(0),
  minimumScale: scale.default(.5),
  bloom: z.boolean().default(true),
  antialiasing: z.enum(['off', 'fxaa', 'msaa']),
  textureFiltering: z.union([z.literal(1), z.literal(4), z.literal(8), z.literal(16)]),
  viewDistance: z.union([z.literal(32), z.literal(64), z.literal(96), z.literal(128)]),
});
export type GraphicsSettings = z.infer<typeof graphicsSchema>;
export type GraphicsPreset = 'low' | 'medium' | 'high';
export const GRAPHICS_PRESETS: Readonly<Record<GraphicsPreset, Readonly<GraphicsSettings>>> = Object.freeze({
  low: Object.freeze({ renderScale: .75, shadows: 'low', localShadows:'off', ambientOcclusion: 'off', aoStrength:.65, reflections:'low', indirectLighting:false, frameLimit:0,targetFPS:0,minimumScale:.5, bloom: false, antialiasing: 'fxaa', textureFiltering: 4, viewDistance: 32 }),
  medium: Object.freeze({ renderScale: 1, shadows: 'medium', localShadows:'low', ambientOcclusion: 'low', aoStrength:.65, reflections:'medium', indirectLighting:true, frameLimit:0,targetFPS:0,minimumScale:.5, bloom: true, antialiasing: 'fxaa', textureFiltering: 8, viewDistance: 64 }),
  high: Object.freeze({ renderScale: 1, shadows: 'high', localShadows:'high', ambientOcclusion: 'high', aoStrength:.65, reflections:'high', indirectLighting:true, frameLimit:0,targetFPS:0,minimumScale:.5, bloom: true, antialiasing: 'msaa', textureFiltering: 16, viewDistance: 96 }),
});
const pacingKeys = new Set(['frameLimit','targetFPS','minimumScale']);
export function graphicsPreset(settings: GraphicsSettings): GraphicsPreset | 'custom' {
  return (Object.keys(GRAPHICS_PRESETS) as GraphicsPreset[]).find(preset =>
    (Object.keys(settings) as (keyof GraphicsSettings)[]).every(key => pacingKeys.has(key) || settings[key] === GRAPHICS_PRESETS[preset][key])) ?? 'custom';
}
export function loadGraphicsSettings(storage?: Pick<Storage, 'getItem'>): GraphicsSettings {
  try {
    const raw = JSON.parse((storage ?? localStorage).getItem(GRAPHICS_STORAGE_KEY) ?? 'null');
    const saved = z.strictObject({ version: z.literal(1), quality: graphicsSchema }).parse(raw);
    const oldKeys = ['renderScale','shadows','ambientOcclusion','antialiasing','textureFiltering','viewDistance'] as const;
    const oldPreset = (Object.keys(GRAPHICS_PRESETS) as GraphicsPreset[]).find(p => oldKeys.every(key=>raw.quality[key]===GRAPHICS_PRESETS[p][key]));
    const defaults = GRAPHICS_PRESETS[oldPreset ?? 'medium'];
    for(const key of ['localShadows','reflections','indirectLighting','bloom'] as const) if(raw.quality[key]===undefined) Object.assign(saved.quality,{[key]:defaults[key]});
    if(raw.quality.localShadows===undefined && !oldPreset) saved.quality.localShadows=saved.quality.shadows==='high'?'high':saved.quality.shadows==='medium'?'low':'off';
    return saved.quality;
  } catch { return { ...GRAPHICS_PRESETS.medium }; }
}
export function saveGraphicsSettings(settings: GraphicsSettings, storage?: Pick<Storage, 'setItem'>): boolean {
  try { (storage ?? localStorage).setItem(GRAPHICS_STORAGE_KEY, JSON.stringify({ version: 1, quality: graphicsSchema.parse(settings) })); return true; }
  catch { return false; }
}

export interface GraphicsCapabilities { hdr: boolean; maxSamples: number; maxAnisotropy: number; maxTextureSize: number }
export function effectiveGraphics(settings: GraphicsSettings, capabilities: GraphicsCapabilities) {
  return {
    ...settings,
    bloom: capabilities.hdr && settings.bloom,
    ambientOcclusion: capabilities.hdr && settings.aoStrength>0 ? settings.ambientOcclusion : 'off' as const,
    reflections: capabilities.hdr ? settings.reflections : 'low' as const,
    indirectLighting: capabilities.hdr && settings.indirectLighting,
    antialiasing: !capabilities.hdr ? 'off' as const : settings.antialiasing === 'msaa' && capabilities.maxSamples < 2 ? 'fxaa' as const : settings.antialiasing,
    samples: capabilities.hdr && settings.antialiasing === 'msaa' && capabilities.maxSamples >= 2 ? Math.min(4, capabilities.maxSamples) : 0,
    textureFiltering: Math.min(settings.textureFiltering, capabilities.maxAnisotropy),
    shadowMapSize: settings.shadows === 'off' ? 0 : Math.min({ low: 1024, medium: 2048, high: 4096 }[settings.shadows], capabilities.maxTextureSize),
  };
}
